import { useEffect, useState } from 'react';
import { InputDate } from 'medere-ui-framework';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { updateLiquidation } from '../../servicesHooks/Honorarium/useHonorariumLiquidationService';
import { parseApiError } from '../../services/apiError';
import { HonorariumLiquidationDTO } from '../../dto/HonorariumLiquidationDTO';
import { parseDateOnly, toDateOnly } from '../../utils/date';
import useNotify from '../../hooks/useNotify';
import FieldError from '../../components/FieldError/FieldError';

type FieldName = 'fromDate' | 'toDate';
type FieldErrors = Partial<Record<FieldName, string>>;

interface EditLiquidationDialogProps {
    /** Liquidación abierta a editar; `null` cierra el diálogo. */
    liquidation: HonorariumLiquidationDTO | null;
    onHide: () => void;
    onSaved: () => void;
}

/** Período y descripción de una liquidación abierta. */
export default function EditLiquidationDialog({ liquidation, onHide, onSaved }: EditLiquidationDialogProps) {
    const notify = useNotify();
    const visible = liquidation != null;

    const [fromDate, setFromDate] = useState<Date | null>(null);
    const [toDate, setToDate] = useState<Date | null>(null);
    const [description, setDescription] = useState('');
    const [errors, setErrors] = useState<FieldErrors>({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!liquidation) return;
        setFromDate(parseDateOnly(liquidation.fromDate));
        setToDate(parseDateOnly(liquidation.toDate));
        setDescription(liquidation.description ?? '');
        setErrors({});
    }, [liquidation]);

    function validate(): FieldErrors {
        const result: FieldErrors = {};
        if (!fromDate) result.fromDate = 'Ingresá la fecha desde';
        if (!toDate) result.toDate = 'Ingresá la fecha hasta';
        if (fromDate && toDate && fromDate > toDate) result.toDate = 'Ingresá una fecha igual o posterior a la fecha desde';
        return result;
    }

    async function handleSave() {
        if (!liquidation) return;
        const validationErrors = validate();
        setErrors(validationErrors);
        if (Object.keys(validationErrors).length > 0) return;

        setSaving(true);
        try {
            await updateLiquidation({
                liquidationId: liquidation.honorariumLiquidation,
                fromDate: toDateOnly(fromDate!),
                toDate: toDateOnly(toDate!),
                description,
            });
            notify.success('Cambios guardados');
            onSaved();
        } catch (error: unknown) {
            const apiError = parseApiError(error);
            // LIQUIDATION_INVALID_PERIOD y VALIDATION_ERROR apuntan al campo
            const fieldErrors: FieldErrors = {};
            for (const detail of apiError.details ?? []) {
                if (detail.field === 'fromDate' || detail.field === 'toDate') fieldErrors[detail.field] = detail.message;
            }
            if (Object.keys(fieldErrors).length > 0) {
                setErrors(fieldErrors);
                return;
            }
            const isBusinessError = apiError.status >= 400 && apiError.status < 500;
            notify.error(isBusinessError ? apiError.message : 'No se pudieron guardar los cambios. Intentá de nuevo.');
        } finally {
            setSaving(false);
        }
    }

    const footer = (
        <div className="md-dialog__footer">
            <Button outlined label="Cancelar" className="md-ghost" onClick={onHide} disabled={saving} />
            <Button label="Guardar cambios" className="md-primary" onClick={handleSave} loading={saving} />
        </div>
    );

    return (
        <Dialog
            header={liquidation ? `Editar liquidación N° ${liquidation.liquidationNumber}` : 'Editar liquidación'}
            visible={visible}
            onHide={() => { if (!saving) onHide(); }}
            footer={footer}
            style={{ width: '560px' }}
            breakpoints={{ '640px': '100vw' }}
            className="md-dialog"
            modal
        >
            <div className="liquidation-form">
                <div className="liquidation-form__row">
                    <div className="liquidation-form__field">
                        <InputDate
                            label="Desde *"
                            value={fromDate}
                            onChange={(e: { value: Date | null }) => { setFromDate(e.value); setErrors(c => ({ ...c, fromDate: undefined })); }}
                        />
                        <FieldError id="edit-liquidation-from-error" message={errors.fromDate} />
                    </div>
                    <div className="liquidation-form__field">
                        <InputDate
                            label="Hasta *"
                            value={toDate}
                            onChange={(e: { value: Date | null }) => { setToDate(e.value); setErrors(c => ({ ...c, toDate: undefined })); }}
                        />
                        <FieldError id="edit-liquidation-to-error" message={errors.toDate} />
                    </div>
                </div>
                <div className="inputContainer flexible--column">
                    <label htmlFor="edit-liquidation-description" className="label inputLabel">Descripción</label>
                    <InputText
                        id="edit-liquidation-description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Ej.: Honorarios de septiembre"
                    />
                </div>
            </div>
        </Dialog>
    );
}
